import { z } from 'zod';

export const MAX_GLB_BYTES = 64 * 1024 * 1024;
export const JobId = z.uuid();
export const MaterialEdit = z.object({
  material: z.string().min(1).max(180),
  metallicFactor: z.number().min(0).max(1),
  roughnessFactor: z.number().min(0).max(1),
}).strict();
export const MaterialInfo = MaterialEdit.extend({ textures: z.number().int().nonnegative() });
export const RecipeRequest = z.object({ recipe: z.literal('ember-forged-v1') }).strict();
export const CaptureName = z.enum(['front', 'three-quarter', 'face', 'profile', 'run', 'attack']);
export const ArtQuality = z.object({
  recipe: z.literal('ember-forged-v1'), recipeSha256: z.string().regex(/^[a-f0-9]{64}$/),
  librarySha256: z.string().regex(/^[a-f0-9]{64}$/), blenderVersion: z.string(),
  technicalStatus: z.literal('passed'), artStatus: z.literal('review_required'),
  captures: z.array(CaptureName).length(6),
});
export const ArtReview = z.object({
  outputSha256: z.string().regex(/^[a-f0-9]{64}$/),
  verdict: z.enum(['review_required', 'changes_required']),
  findings: z.array(z.object({
    area: z.enum(['identity', 'anatomy', 'hair', 'materials', 'deformation', 'runtime']),
    severity: z.enum(['P1', 'P2']), summary: z.string().min(1).max(500),
    evidence: z.array(CaptureName).min(1).max(6), nextAction: z.string().min(1).max(500),
  }).strict()).max(20),
}).strict().refine(r => r.verdict !== 'changes_required' || r.findings.length > 0, '수정 사유가 필요합니다.');
export const ReviewRequest = z.object({ revision: z.number().int().positive(), review: ArtReview }).strict();
export const Summary = z.object({
  triangles: z.number().int().nonnegative(), meshes: z.number().int().nonnegative(),
  bones: z.number().int().nonnegative(), animations: z.array(z.string()),
  materials: z.array(MaterialInfo), images: z.number().int().nonnegative(),
});
export const Revision = z.object({
  id: z.number().int().positive(), createdAt: z.string(), note: z.string(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/), summary: Summary, quality: ArtQuality.optional(),
  artReview: ArtReview.optional(), reviewedAt: z.string().optional(),
});
export const Job = z.object({
  id: JobId, label: z.string(), createdAt: z.string(),
  status: z.enum(['queued', 'running', 'ready', 'failed']),
  operation: z.string(), error: z.string().nullable(),
  sourceSha256: z.string(), currentRevision: z.number().int().nonnegative(),
  revisions: z.array(Revision),
});
export type BlenderJob = z.infer<typeof Job>;
export type MaterialUpdate = z.infer<typeof MaterialEdit>;
export type AssetSummary = z.infer<typeof Summary>;
export const artifactUrl = (id: string, revision: number, kind: 'glb' | 'blend') =>
  `/api/blender/jobs/${id}/revisions/${revision}/model.${kind}`;
export const inspectionUrl = (id: string, revision: number, capture?: z.infer<typeof CaptureName>) =>
  `/api/blender/jobs/${id}/revisions/${revision}/${capture ? `inspection/${capture}.png` : 'inspection.json'}`;
